import { streamRequest, getRequest, postRequest } from './api';

/**
 * Knowledge Service API
 */

/**
 * Stream chat with knowledge agent
 * @param {string} question - User question
 * @param {Array} conversationHistory - Previous conversation messages
 * @param {Function} onMessage - Callback for each SSE message
 * @param {Function} onError - Callback for errors
 * @param {Function} onComplete - Callback when stream completes
 * @param {string} modelKey - Optional model key to use
 */
export async function streamKnowledgeChat(question, conversationHistory, onMessage, onError, onComplete, modelKey = null, userEmail = null, userProfile = null, sessionId = null) {
  const payload = { 
    message: question,
    conversation_history: conversationHistory 
  };
  if (modelKey) payload.model_key = modelKey;
  if (userEmail) payload.user_email = userEmail;
  if (userProfile) payload.user_profile = userProfile;
  if (sessionId) payload.session_id = sessionId;
  await streamRequest('knowledge', '/query', payload, onMessage, onError, onComplete);
}

/**
 * Get knowledge base status
 * @returns {Promise<object>} Response data with status information
 */
export async function getKnowledgeStatus() {
  return await getRequest('knowledge', '/status');
}

/**
 * Get list of all knowledge base documents
 * @returns {Promise<object>} Response with documents array and metadata
 */
export async function getKnowledgeDocuments() {
  return await getRequest('knowledge', '/documents');
}

/**
 * Get content of a specific document
 * @param {string} filePath - Relative path to the document
 * @returns {Promise<object>} Document content and metadata
 */
export async function getDocumentContent(filePath) {
  return await getRequest('knowledge', `/document/${encodeURIComponent(filePath)}`);
}

/**
 * Trigger reindexing of the knowledge base
 * @param {object} options - Indexing options
 * @param {string[]} options.folders - Array of folder names to index (optional)
 * @param {number} options.chunk_size - Chunk size in characters (default: 1000)
 * @param {number} options.overlap - Overlap between chunks (default: 150)
 * @returns {Promise<object>} Reindex status
 */
export async function reindexKnowledgeBase(options = {}) {
  return await postRequest('knowledge', '/reindex', {
    folders: options.folders || null,
    chunk_size: options.chunk_size || 1000,
    overlap: options.overlap || 150
  });
}

/**
 * Get agent information (system prompt and tools)
 * @returns {Promise<object>} Response data with agent info
 */
export async function getAgentInfo() {
  return await getRequest('knowledge', '/agent/info');
}

/**
 * Get Knowledge Agent Q&A history
 * @param {object} filters - Filter parameters (limit, session_id, user)
 * @returns {Promise<object>} Response data with history records
 */
export async function getKnowledgeQAHistory(filters = {}) {
  return await getRequest('knowledge', '/qa/history', filters);
}

/**
 * Submit feedback for a Knowledge Agent Q&A
 * @param {number} qaId - Q&A record ID
 * @param {string} feedback - 'correct' or 'incorrect'
 * @returns {Promise<object>} Response data
 */
export async function submitKnowledgeFeedback(qaId, feedback) {
  return await postRequest('knowledge', '/qa/feedback', { record_id: qaId, feedback });
}

/**
 * Get Knowledge Agent Q&A statistics
 * @returns {Promise<object>} Statistics data
 */
export async function getKnowledgeQAStatistics() {
  return await getRequest('knowledge', '/qa/statistics');
}

/**
 * Mark a Knowledge Agent Q&A answer as correct or incorrect
 * @param {number} qaId - Q&A record ID
 * @param {boolean} isCorrect - True if correct, false if incorrect
 * @returns {Promise<object>} Response data
 */
export async function markKnowledgeQAAnswer(qaId, isCorrect) {
  return await postRequest('knowledge', '/qa/mark', { qa_id: qaId, is_correct: isCorrect });
}

export default {
  streamKnowledgeChat,
  getKnowledgeStatus,
  getKnowledgeDocuments,
  getDocumentContent,
  reindexKnowledgeBase,
  getAgentInfo,
  getKnowledgeQAHistory,
  submitKnowledgeFeedback,
  getKnowledgeQAStatistics,
  markKnowledgeQAAnswer,
};
